import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { format, subDays, startOfDay, endOfDay } from 'date-fns';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Legend, AreaChart, Area, BarChart, Bar } from 'recharts';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import api, { endpoints } from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';

// Custom tooltip component that positions itself based on screen position
const SmartPositionTooltip = (props) => {
  const { active, payload, label, coordinate } = props;
  
  if (!active || !payload || !payload.length) {
    return null;
  }
  
  // Force tooltip to always appear on the left side for right-edge columns
  // Make tooltip wider so long model names fit on one line
  const tooltipWidth = 340; // px
  const tooltipStyle = {
    backgroundColor: '#fff',
    padding: '10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    position: 'absolute',
    zIndex: 1000,
    pointerEvents: 'none',
    width: `${tooltipWidth}px`,
    maxWidth: `${tooltipWidth}px`,
    right: 'auto', // Never position from right
    left: Math.max(10, coordinate.x - (tooltipWidth + 20)), // Position to the left of cursor with margin
    top: coordinate.y - 10,
    whiteSpace: 'nowrap'
  };
  
  return (
    <div style={tooltipStyle}>
      <p style={{ margin: '0 0 5px', fontWeight: 'bold' }}>{`Date: ${label}`}</p>
      {payload.map((entry, index) => (
        <p key={`item-${index}`} style={{ margin: '2px 0', color: entry.color, whiteSpace: 'nowrap' }}>
          {`${entry.name}: ${entry.value}`}
        </p>
      ))}
    </div>
  );
};

// Tooltip content with position controlled by Tooltip.position
const ClampedTooltipContent = ({ active, payload, label, seriesOrder }) => {
  if (!active || !payload || payload.length === 0) return null;
  const items = Array.isArray(payload) ? [...payload] : [];
  if (Array.isArray(seriesOrder) && seriesOrder.length > 0) {
    const orderMap = new Map(seriesOrder.map((name, idx) => [name, idx]));
    items.sort((a, b) => {
      const ai = orderMap.has(a.name) ? orderMap.get(a.name) : Number.POSITIVE_INFINITY;
      const bi = orderMap.has(b.name) ? orderMap.get(b.name) : Number.POSITIVE_INFINITY;
      return bi - ai; // Higher index (drawn on top) first
    });
  }
  return (
    <div style={{
      backgroundColor: '#fff',
      padding: '10px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      pointerEvents: 'none',
      whiteSpace: 'nowrap',
      maxWidth: 360
    }}>
      <p style={{ margin: '0 0 5px', fontWeight: 'bold' }}>{`Date: ${label}`}</p>
      {items.map((entry, index) => (
        <p key={`item-${index}`} style={{ margin: '2px 0', color: entry.color }}>
          {`${entry.name}: ${entry.value}`}
        </p>
      ))}
    </div>
  );
};

// High-contrast, colorblind-friendly palette (Tableau 10)
const COLORS = ['#E15759', '#4E79A7', '#59A14F', '#F28E2B', '#B07AA1', '#76B7B2'];

const DeveloperDetails = () => {
  const { userId } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: startOfDay(subDays(new Date(), 30)),
    endDate: endOfDay(new Date())
  });

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const start = format(dateRange.startDate, 'yyyy-MM-dd');
      const end = format(dateRange.endDate, 'yyyy-MM-dd');
      const res = await api.get(endpoints.analytics.developerDetails(userId), {
        params: { startDate: start, endDate: end }
      });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, dateRange.startDate, dateRange.endDate]);

  if (loading) return <LoadingSpinner />;
  if (error) return <div className="p-6 text-red-600">{error}</div>;
  if (!data) return <div className="p-6">No data</div>;

  const { summary, charts, breakdown, trends, top, recentEvents } = data;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {data?.user?.MemberName || (data?.user?.Email ? data.user.Email.split('@')[0] : 'Developer')}
          </h1>
          <p className="text-gray-600">
            {data?.user?.Email && <span className="mr-2">{data.user.Email}</span>}
            {data?.user?.TeamName && <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">{data.user.TeamName}</span>}
          </p>
          <p className="text-gray-500 text-sm mt-1">User ID: {userId}</p>
        </div>
        <Link to="/users" className="text-blue-600 hover:underline">Back to Developers</Link>
      </div>

      <div className="bg-white rounded-lg shadow-sm border p-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Date Range:</span>
          <DatePicker selected={dateRange.startDate} onChange={(d) => setDateRange(r => ({...r, startDate: d}))} className="px-3 py-1 border border-gray-300 rounded text-sm" dateFormat="MMM d, yyyy" />
          <span className="text-gray-500">to</span>
          <DatePicker selected={dateRange.endDate} onChange={(d) => setDateRange(r => ({...r, endDate: d}))} className="px-3 py-1 border border-gray-300 rounded text-sm" dateFormat="MMM d, yyyy" />
        </div>
      </div>

      {/* KPI Cards Row 1 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Total Requests */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <p className="text-sm text-gray-600">Total Requests</p>
          <p className="text-2xl font-bold">{(summary.totalRequests || 0).toLocaleString()}</p>
          <p className="text-sm text-gray-500 mt-1">Active Days: {summary.activeDays || 0} • Requests/Day: {summary.requestsPerDay || 0}</p>
        </div>
        {/* Acceptance & Tabs */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <p className="text-sm text-gray-600">Acceptance Rate</p>
          <p className="text-2xl font-bold">{(breakdown?.acceptanceRate || 0)}%</p>
          <p className="text-sm text-gray-500 mt-1">Tabs Acceptance: {(breakdown?.tabsAcceptanceRate || 0)}%</p>
        </div>
        {/* Tokens */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <p className="text-sm text-gray-600">Total Tokens (K)</p>
          <p className="text-2xl font-bold">{(summary.totalTokensK || 0).toLocaleString()}</p>
          <p className="text-sm text-gray-600 mt-1">Token Cost ($)</p>
          <p className="text-xl font-semibold">${(summary.totalCostDollars || 0).toFixed(2)}</p>
          <p className="text-sm text-gray-600 mt-1">Cost per 100k Tokens ($)</p>
          <p className="text-xl font-semibold">${(summary.costPer100kTokens || 0).toFixed(2)}</p>
        </div>
      </div>

      {/* KPI Cards Row 2: Request types (no Bugbot) */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {[
          { label: 'Composer', value: breakdown?.composerRequests },
          { label: 'Chat', value: breakdown?.chatRequests },
          { label: 'Agent', value: breakdown?.agentRequests },
          { label: 'Cmd+K', value: breakdown?.cmdkUsages }
        ].map((kpi) => (
          <div key={kpi.label} className="bg-white rounded-lg shadow-sm border p-6">
            <p className="text-sm text-gray-600">{kpi.label} Requests</p>
            <p className="text-2xl font-bold">{(kpi.value || 0).toLocaleString()}</p>
          </div>
        ))}
      </div>

      {/* Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4">Requests & Acceptance Trend</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={(trends?.requestsByDay || []).map(d => ({ ...d, date: d.date?.slice(0,10) }))}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis 
                yAxisId="requests"
                label={{ 
                  value: 'Requests', 
                  angle: -90, 
                  position: 'insideLeft',
                  style: { textAnchor: 'middle' }
                }}
              />
              <YAxis 
                yAxisId="lines"
                orientation="right"
                label={{ 
                  value: 'Lines Added', 
                  angle: 90, 
                  position: 'insideRight',
                  style: { textAnchor: 'middle' }
                }}
              />
              <Tooltip content={<SmartPositionTooltip />} />
              <Legend />
              <Line yAxisId="requests" type="monotone" dataKey="composer" stroke="#ef4444" name="Composer" />
              <Line yAxisId="requests" type="monotone" dataKey="chat" stroke="#3b82f6" name="Chat" />
              <Line yAxisId="requests" type="monotone" dataKey="agent" stroke="#10b981" name="Agent" />
              <Line yAxisId="requests" type="monotone" dataKey="cmdk" stroke="#f59e0b" name="Cmd+K" />
              <Line yAxisId="lines" type="monotone" dataKey="linesAdded" stroke="#9333ea" name="Lines Added" />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4">Tokens (K) & Cost ($) Trend</h3>
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={(trends?.tokensCostByDay || []).map(d => ({ ...d, date: d.date?.slice(0,10), tokensK: (d.tokens||0)/1000, dollars: (d.cents||0)/100 }))}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip content={<SmartPositionTooltip />} />
              <Legend />
              <Area type="monotone" dataKey="tokensK" stroke="#3b82f6" fill="#93c5fd" name="Tokens (K)" />
              <Area type="monotone" dataKey="dollars" stroke="#10b981" fill="#6ee7b7" name="Cost ($)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Distribution & Top tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4">Event Type (Usage-Based vs Included)</h3>
          {charts.eventTypes && charts.eventTypes.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie dataKey="count" data={charts.eventTypes} nameKey="label" cx="50%" cy="50%" outerRadius={100} label>
                  {charts.eventTypes.map((entry, index) => (
                    <Cell key={`et-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<SmartPositionTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-gray-500">No event data</div>
          )}
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4">Top Models</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Events</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tokens (K)</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cost ($)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {(top?.models || []).map((m, i) => (
                  <tr key={i}>
                    <td className="px-4 py-2 text-sm text-gray-900 truncate max-w-[240px]" title={m.model}>{m.model}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{(m.events || 0).toLocaleString()}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{(((m.tokens||0)/1000)).toLocaleString()}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">${(((m.cents||0)/100).toFixed(2))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4">Model Usage Trend (Daily)</h3>
          <ModelUsageTrend data={trends?.dailyModelUsage} topModels={(top?.models || []).map(m => m.model)} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Timestamp</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">In</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Out</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cost ($)</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Accepted</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {(recentEvents || []).map((e, i) => (
                  <tr key={i}>
                    <td className="px-4 py-2 text-sm text-gray-900">{e.EventTimestamp ? new Date(e.EventTimestamp).toLocaleString() : ''}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{e.EventType}</td>
                    <td className="px-4 py-2 text-sm text-gray-900 truncate max-w-[200px]" title={e.ModelUsed}>{e.ModelUsed}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{(e.InputTokens || 0).toLocaleString()}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{(e.OutputTokens || 0).toLocaleString()}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">${(((e.TotalCents||0)/100).toFixed(2))}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{e.WasAccepted ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced color palette for better visualization
// Distinct, accessible palette for stacked models (Tableau 10 extended)
const palette = [
  '#4E79A7', // blue
  '#F28E2B', // orange
  '#E15759', // red
  '#76B7B2', // teal
  '#59A14F', // green
  '#EDC948', // yellow
  '#B07AA1', // purple
  '#FF9DA7', // pink
  '#9C755F', // brown
  '#BAB0AC'  // gray
];

function normalizeModelName(name) {
  return (name || 'Unknown').trim().toLowerCase();
}

function buildModelStackFromRows(rows, inputTopModels = [], topN = 4) {
  const dataRows = Array.isArray(rows) ? rows : [];

  // Map normalized -> display name (prefer provided labels)
  const providedDisplay = new Map();
  (inputTopModels || []).forEach(m => {
    const norm = normalizeModelName(m);
    if (!providedDisplay.has(norm)) providedDisplay.set(norm, m);
  });

  // Count totals per normalized model and capture first seen display
  const totals = new Map();
  const seenDisplay = new Map();
  for (const r of dataRows) {
    const norm = normalizeModelName(r?.model);
    const disp = r?.model || 'Unknown';
    if (!seenDisplay.has(norm)) seenDisplay.set(norm, disp);
    totals.set(norm, (totals.get(norm) || 0) + (Number(r?.events || 0)));
  }

  // Determine top normalized list
  const topNorm = (Array.isArray(inputTopModels) && inputTopModels.length > 0)
    ? inputTopModels.map(normalizeModelName).slice(0, topN)
    : Array.from(totals.entries())
        .sort((a,b) => b[1]-a[1])
        .slice(0, topN)
        .map(([norm]) => norm);

  // Build mapping to pretty labels
  const labelFor = (norm) => providedDisplay.get(norm) || seenDisplay.get(norm) || norm;

  // Aggregate by day
  const byDate = new Map(); // date -> { date, normKey -> count, other -> count }
  for (const r of dataRows) {
    const raw = r?.date || r?.EventDate || r?.Date;
    let d = '';
    if (raw instanceof Date) d = raw.toISOString().slice(0,10);
    else if (typeof raw === 'string') d = raw.slice(0,10);
    if (!d) continue;

    const norm = normalizeModelName(r?.model);
    const bucket = topNorm.includes(norm) ? norm : 'other';
    const ev = Number(r?.events || 0);

    const row = byDate.get(d) || { date: d };
    row[bucket] = (row[bucket] || 0) + ev;
    byDate.set(d, row);
  }

  // Produce series display labels
  const series = [...topNorm.map(labelFor), 'Other'];

  // Remap to display keys for chart data
  const chartData = Array.from(byDate.values())
    .sort((a,b) => a.date.localeCompare(b.date))
    .map(row => {
      const out = { date: row.date };
      for (const n of topNorm) {
        const disp = labelFor(n);
        out[disp] = row[n] || 0;
      }
      out['Other'] = row['other'] || 0;
      return out;
    });

  return { series, chartData };
}

const ModelUsageTrend = ({ data, topModels = [] }) => {
  // Hooks must be at top level
  const containerRef = useRef(null);
  const [tooltipPos, setTooltipPos] = useState(null);

  // Empty state early-return (hooks already declared)
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-8 border border-dashed border-gray-300 rounded-lg">
        <p className="text-gray-500 mb-2">No model usage data available for the selected date range</p>
        <p className="text-gray-400 text-sm">Try selecting a different date range or check if this developer has used any AI models</p>
      </div>
    );
  }

  let series = [];
  let formattedChartData = [];
  try {
    const stack = buildModelStackFromRows(data, topModels, 4);
    series = stack.series;
    formattedChartData = stack.chartData.map(item => {
      try {
        const dateOnly = typeof item.date === 'string' ? item.date.slice(0, 10) : item.date;
        const dateObj = typeof dateOnly === 'string' ? new Date(`${dateOnly}T12:00:00`) : dateOnly;
        return { ...item, formattedDate: format(dateObj, 'MMM dd') };
      } catch (e) {
        return { ...item, formattedDate: String(item.date).slice(0, 10) };
      }
    });
  } catch (e) {
    console.error("Error rendering ModelUsageTrend:", e);
    return (
      <div className="text-center py-8 border border-dashed border-gray-300 rounded-lg">
        <p className="text-red-500 mb-2">Error displaying model usage data</p>
        <p className="text-gray-500 text-sm">{e.message || 'An unexpected error occurred'}</p>
        <button 
          className="mt-3 px-4 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-sm"
          onClick={() => window.location.reload()}
        >
          Reload page
        </button>
      </div>
    );
  }

  const computeAndSetTooltipPos = (e) => {
    if (!containerRef.current || !e) return;
    const rect = containerRef.current.getBoundingClientRect();
    const containerWidth = rect.width;
    const containerHeight = rect.height;
    const approxWidth = 340;
    const lines = (e.activePayload?.length || 1);
    const approxHeight = 32 + lines * 20 + 12;
    let x = (e.chartX || 0) + 12;
    let y = (e.chartY || 0) - approxHeight - 12;
    if (x + approxWidth > containerWidth) x = containerWidth - approxWidth - 8;
    if (x < 8) x = 8;
    if (y < 8) y = Math.min(containerHeight - approxHeight - 8, (e.chartY || 0) + 12);
    if (y < 8) y = 8;
    setTooltipPos({ x: Math.round(x), y: Math.round(y) });
  };

  return (
    <div ref={containerRef} style={{ width: '100%', height: 300 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart 
          data={formattedChartData}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
          onMouseMove={computeAndSetTooltipPos}
          onMouseLeave={() => setTooltipPos(null)}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
          <XAxis 
            dataKey="formattedDate" 
            tick={{ fontSize: 12 }}
            tickLine={false}
          />
          <YAxis 
            label={{ value: 'Usage Count', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
            tick={{ fontSize: 12 }}
          />
          <Tooltip 
            content={(props) => <ClampedTooltipContent {...props} seriesOrder={series} />}
            position={tooltipPos || undefined}
            wrapperStyle={{ pointerEvents: 'none' }}
            allowEscapeViewBox={{ x: true, y: true }}
            cursor={{ strokeDasharray: '3 3' }}
          />
          <Legend 
            verticalAlign="bottom" 
            height={36}
            iconType="circle"
          />
          {series.map((name, i) => (
            <Bar 
              key={name} 
              dataKey={name} 
              stackId="models" 
              fill={palette[i % palette.length]} 
              name={name}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default DeveloperDetails;


